import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import '../App.css';

/**
 * Privacy Policy Page
 * This component displays the privacy policy and can be accessed directly without authentication.
 * CRITICAL for Twilio compliance: Must state that opt-in data is NOT shared with third parties.
 * Uses the logged-in user's theme or the system default theme.
 */
function PrivacyPolicy() {
  const { applyTheme } = useTheme();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTheme = async () => {
      try {
        const token = localStorage.getItem('token');

        if (token) {
          // User is logged in - get their theme preference
          try {
            const response = await api.get('/preferences');
            const userTheme = response.data.find(pref => pref.preference_key === 'theme');
            if (userTheme) {
              applyTheme(userTheme.preference_value);
            }
          } catch (error) {
            console.error('Error loading user theme:', error);
            // Fall back to default theme
            await loadDefaultTheme();
          }
        } else {
          // User not logged in - get system default theme
          await loadDefaultTheme();
        }
      } catch (error) {
        console.error('Error loading theme:', error);
      } finally {
        setLoading(false);
      }
    };

    const loadDefaultTheme = async () => {
      try {
        const response = await api.get('/admin/theme');
        if (response.data.defaultTheme) {
          applyTheme(response.data.defaultTheme);
        }
      } catch (error) {
        console.error('Error loading default theme:', error);
      }
    };

    loadTheme();
  }, [applyTheme]);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--color-background)', padding: '2rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', backgroundColor: 'var(--color-bg)', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h1 style={{ marginBottom: '1.5rem', fontSize: '2rem', color: 'var(--color-primary)' }}>
          Privacy Policy
        </h1>

        <div style={{ lineHeight: '1.8', color: 'var(--color-text)' }}>
          <p style={{ marginBottom: '1.5rem', fontSize: '0.875rem', color: 'var(--color-text-light)' }}>
            Last Updated: December 15, 2024
          </p>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Introduction
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              Welcome to Brings to Mind ("we," "our," or "us"). We are committed to protecting your privacy
              and handling your personal information with care and respect. This Privacy Policy explains how
              we collect, use, disclose, and safeguard your information when you use our service.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              By using Brings to Mind, you agree to the collection and use of information in accordance with
              this policy. If you do not agree with our policies and practices, please do not use our service.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Information We Collect
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              We collect several types of information to provide and improve our service:
            </p>

            <h3 style={{ fontSize: '1.2rem', marginBottom: '0.75rem', marginTop: '1.5rem', color: 'var(--color-heading)' }}>
              Personal Information
            </h3>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Account Information:</strong> Username, email address, phone number (optional), and password (encrypted)
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Profile Information:</strong> Timezone, profile photo, and other optional profile details
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Content:</strong> Reflections, recordings, and other content you create and store in our service
              </li>
            </ul>

            <h3 style={{ fontSize: '1.2rem', marginBottom: '0.75rem', marginTop: '1.5rem', color: 'var(--color-heading)' }}>
              Usage Information
            </h3>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>Log data (IP address, browser type, pages visited)</li>
              <li style={{ marginBottom: '0.5rem' }}>Device information</li>
              <li style={{ marginBottom: '0.5rem' }}>Usage patterns and preferences</li>
            </ul>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              How We Use Your Information
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              We use the information we collect for the following purposes:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>To provide, maintain, and improve our service</li>
              <li style={{ marginBottom: '0.5rem' }}>To authenticate your identity and secure your account</li>
              <li style={{ marginBottom: '0.5rem' }}>To send you verification codes and security notifications</li>
              <li style={{ marginBottom: '0.5rem' }}>To send reflection reminders (only if you opt in)</li>
              <li style={{ marginBottom: '0.5rem' }}>To respond to your requests and support inquiries</li>
              <li style={{ marginBottom: '0.5rem' }}>To detect and prevent fraud, abuse, and security incidents</li>
              <li style={{ marginBottom: '0.5rem' }}>To comply with legal obligations</li>
            </ul>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              SMS Communications and Consent
            </h2>
            <p style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--color-background-secondary)', borderRadius: '0.5rem', border: '2px solid var(--color-primary)' }}>
              <strong>IMPORTANT - Twilio Compliance:</strong> Your phone number and SMS opt-in consent data
              will <strong>NEVER</strong> be shared with, sold to, or rented to any third parties for marketing
              or any other purposes. We maintain strict control over your consent information.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              If you provide a phone number and consent to SMS communications:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Your phone number is used ONLY for account security (verification codes, password resets)
                and optional reflection reminders if you enable them
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                We use Twilio, a trusted third-party SMS service provider, to deliver text messages
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Your consent is recorded with a timestamp and IP address for compliance purposes
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                You can revoke SMS consent at any time by replying STOP or updating your settings
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                We will never send marketing messages or promotional content via SMS
              </li>
            </ul>
            <p style={{ marginBottom: '1rem' }}>
              For more details, see our{' '}
              <a
                href="?page=sms-opt-in"
                style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
              >
                SMS Terms & Conditions
              </a>
              .
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Information Sharing and Disclosure
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              We do not sell, rent, or share your personal information with third parties except in the
              following limited circumstances:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Service Providers:</strong> We may share information with trusted service providers
                who assist us in operating our service (e.g., Twilio for SMS delivery, cloud hosting providers).
                These providers are contractually obligated to keep your information secure and use it only
                for the purposes we specify.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Legal Requirements:</strong> We may disclose information if required by law, court order,
                or government regulation, or if we believe disclosure is necessary to protect our rights or
                the safety of our users.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>With Your Consent:</strong> If you explicitly share reflections with other users through
                our sharing features, we will share that content with the users you specify.
              </li>
            </ul>
            <p style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
              We will NEVER share, sell, or rent your opt-in consent data or phone number to third parties
              for marketing purposes.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Data Security
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              We implement industry-standard security measures to protect your information:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>Passwords are encrypted using bcrypt hashing</li>
              <li style={{ marginBottom: '0.5rem' }}>HTTPS encryption for all data in transit</li>
              <li style={{ marginBottom: '0.5rem' }}>Secure database storage with access controls</li>
              <li style={{ marginBottom: '0.5rem' }}>Regular security audits and updates</li>
            </ul>
            <p style={{ marginBottom: '1rem' }}>
              However, no method of transmission over the Internet is 100% secure. While we strive to
              protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Your Rights and Choices
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              You have the following rights regarding your personal information:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Access:</strong> You can access and review your personal information in your account settings
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Correction:</strong> You can update or correct your information at any time
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Deletion:</strong> You can delete your account and all associated data through account settings
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>SMS Opt-Out:</strong> You can revoke SMS consent by replying STOP, removing your phone number,
                or changing your verification method to email
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Data Portability:</strong> You can export your reflections and other content
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Data Retention
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              We retain your information for as long as your account is active or as needed to provide
              our services. If you delete your account, we will delete your personal information within
              30 days, except:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>Information we are required to retain for legal or regulatory purposes</li>
              <li style={{ marginBottom: '0.5rem' }}>Anonymized data used for analytics</li>
              <li style={{ marginBottom: '0.5rem' }}>Backup copies deleted according to our backup retention schedule</li>
            </ul>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Children's Privacy
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              Brings to Mind is not intended for children under 13 years of age. We do not knowingly
              collect personal information from children under 13. If you believe we have collected
              information from a child under 13, please contact us immediately.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Changes to This Privacy Policy
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              We may update this Privacy Policy from time to time. We will notify you of any material
              changes by:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>Posting the new policy on this page</li>
              <li style={{ marginBottom: '0.5rem' }}>Updating the "Last Updated" date</li>
              <li style={{ marginBottom: '0.5rem' }}>Sending you an email notification (for significant changes)</li>
            </ul>
            <p style={{ marginBottom: '1rem' }}>
              Your continued use of the service after changes are posted constitutes your acceptance
              of the updated policy.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Contact Us
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              If you have questions or concerns about this Privacy Policy or our privacy practices,
              please contact us through your account settings or at the support email provided in your
              account dashboard.
            </p>
          </section>

          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--color-border)' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-light)' }}>
              Last Updated: December 15, 2024
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-light)', marginTop: '0.5rem' }}>
              Version: 1.0
            </p>
          </div>

          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <a
              href="/"
              style={{
                padding: '0.75rem 2rem',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-base, 0.5rem)',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                textDecoration: 'none',
                display: 'inline-block'
              }}
            >
              Back to Main
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
